import { useState, useEffect } from 'react';
import { Box, Button, Typography, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Card, CardContent } from '@mui/material';
import { DataGrid, GridToolbarQuickFilter } from '@mui/x-data-grid';
import { useAuth } from '../../AuthContext';
import { tokens } from '../../theme';
import { useTheme } from '@mui/material';
import { toast } from 'react-toastify';
import LaptopIcon from '@mui/icons-material/Laptop';
import SendIcon from '@mui/icons-material/Send';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

const Borrow = ({ userId }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { getData, addData, updateData, deleteData } = useAuth();
  const [borrows, setBorrows] = useState([]);
  const [items, setItems] = useState([]);
  const [openBorrowDialog, setOpenBorrowDialog] = useState(false);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [selectedBorrowId, setSelectedBorrowId] = useState(null);
  const [formData, setFormData] = useState({
    equipment: '',
    duration: '',
    type: '',
    destination: '',
    description: '',
    date: ''
  });
  const [isEditMode, setIsEditMode] = useState(false);
  const [loading, setLoading] = useState(true);

  const statusText = {
    0: 'Pending',
    1: 'Inprogress',
    2: 'Approved',
    3: 'Rejected',
    4: 'Returned'
  };

  const statusColor = {
    0: colors.orange[100],
    1: colors.green[100],
    2: colors.blue[100],
    3: colors.red[100],
    4: colors.blue[100]
  };

  useEffect(() => {
    async function fetchData() {
      try {
        const borrowResponse = await getData('proccess.php', 'borrow_V2', userId);
        if (borrowResponse.success) {
          setBorrows(borrowResponse.data);
        }

        const inventoryResponse = await getData('proccess.php', 'inventory');
        if (inventoryResponse.success) {
          setItems(inventoryResponse.data);
        }
      } catch (err) {
        toast.error('Network error: Failed to fetch data');
        console.error('Fetch error:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [userId, getData]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleBorrowSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditMode) {
        const response = await updateData('proccess.php', 'borrow_V2', {
          staff_id: userId,
          date: formData.date,
          equipment: formData.equipment,
          duration: formData.duration,
          type: formData.type,
          destination: formData.destination,
          description: formData.description,
          status: '0'
        }, selectedBorrowId);
        if (response.success) {
          toast.success('Borrow request updated successfully');
          setBorrows(borrows.map(borrow => 
            borrow.id === selectedBorrowId ? { ...borrow, ...formData } : borrow
          ));
          setOpenBorrowDialog(false);
          setFormData({ equipment: '', duration: '', type: '', destination: '', description: '', date: '' });
          setIsEditMode(false);
          setSelectedBorrowId(null);
        } else {
          toast.error(response.message || 'Failed to update borrow request');
        }
      } else {
        const response = await addData('proccess.php', 'borrow_V2', {
          staff_id: userId,
          equipment: formData.equipment,
          duration: formData.duration,
          type: formData.type,
          destination: formData.destination,
          description: formData.description
        });
        
        if (response.success) {
          toast.success('Borrow request submitted successfully');
          setBorrows(response.data);
          setOpenBorrowDialog(false);
          setFormData({ equipment: '', duration: '', type: '', destination: '', description: '', date: '' });
        } else {
          toast.error(response.message || 'Failed to submit borrow request');
        }
      }
    } catch (err) {
      toast.error('Network error: Failed to submit borrow request');
      console.error('Borrow submit error:', err);
    }
  };

  const handleEditBorrow = (borrow) => {
    setIsEditMode(true);
    setSelectedBorrowId(borrow.id);
    setFormData({
      equipment: borrow.equipment,
      duration: borrow.duration,
      type: borrow.type,
      destination: borrow.destination,
      description: borrow.description,
      date: borrow.date
    });
    setOpenBorrowDialog(true);
  };

  const handleCancelBorrow = async () => {
    try {
      const response = await deleteData('proccess.php', 'borrow_V2', selectedBorrowId);
      if (response.success) {
        toast.success('Borrow request cancelled successfully');
        setBorrows(borrows.filter(borrow => borrow.id !== selectedBorrowId));
        setOpenCancelDialog(false);
        setSelectedBorrowId(null);
      } else {
        toast.error(response.message || 'Failed to cancel borrow request');
      }
    } catch (err) {
      toast.error('Network error: Failed to cancel borrow request');
      console.error('Cancel error:', err);
    }
  };

  const columns = [
    { field: 'id', headerName: 'ID', flex: 0.5, minWidth: 50 },
    { field: 'equipment', headerName: 'Equipment', flex: 1, minWidth: 120 },
    { field: 'date', headerName: 'Borrow Date', flex: 1, minWidth: 120 },
    { field: 'duration', headerName: 'Duration', flex: 1, minWidth: 120 },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Typography>{statusText[params.row.status] || 'Unknown'}</Typography>
      )
    }
  ];

  const availableEquipment = items.filter(item => (item.Type === 'Laptop' || item.Type === 'Charger') && item.status === 0);
  const currentBorrows = borrows.filter(borrow => borrow.status < 4);

  return (
    <Box mt={2}>
      {loading ? (
        <Typography>Loading...</Typography>
      ) : (
        <>
          <Box width="100%" display="flex" justifyContent="end">
            <Button
              variant="contained"
              startIcon={<LaptopIcon />}
              onClick={() => {
                setIsEditMode(false);
                setFormData({ equipment: '', duration: '', type: '', destination: '', description: '', date: '' });
                setOpenBorrowDialog(true);
              }}
              sx={{ mb: 2, backgroundColor: colors.blue[400], '&:hover': { backgroundColor: colors.blue[500] } }}
            >
              Borrow Equipment
            </Button>
          </Box>
          <Box display="flex" justifyContent="space-between" flexWrap="wrap-reverse">
            <Box paddingBottom="1rem" flex="1" maxWidth="1000px" minWidth="350px" m="0.3rem">
              <Typography variant="h5" fontWeight={700}>Available Laptops/Chargers</Typography>
              <Box paddingTop="1rem" height={'40vh'} overflow={'auto'}>
                {availableEquipment.length > 0 ? (
                  availableEquipment.map(item => (
                    <Box key={item.id}>
                      <Card sx={{ backgroundColor: statusColor[item.status], color: 'black', mb: '1rem' }}>
                        <CardContent>
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="h5" fontWeight={700}>
                              {item.id}
                            </Typography>
                            <Typography variant="h4" fontWeight={700} color='green'>
                              Available
                            </Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between" p="1rem 0 0 0">
                            <Typography>
                              Type: {item.Type}
                            </Typography>
                            <Typography variant="h5" fontWeight={700} color="blue">
                              {item.name}
                            </Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between" p="1rem 0 1rem 0">
                            <Typography>
                              Last Updated: {item.dateCreated || 'N/A'}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Box>
                  ))
                ) : (
                  <Typography>No Available Laptops/Chargers</Typography>
                )}
              </Box>
            </Box>

            <Box flex="1" maxWidth="700px" minWidth="350px" m="0.3rem" >
              <Typography variant="h5" fontWeight={700}>Current Borrowing</Typography>
              <Box paddingTop="1rem" height={'40vh'} overflow={'auto'}>
                {currentBorrows.length > 0 ? (
                  currentBorrows.map(borrow => (
                    <Box key={borrow.id}>
                      <Card sx={{ backgroundColor: statusColor[borrow.status], color: 'black', mb: '1rem' }}>
                        <CardContent>
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="h5" fontWeight={700}>
                              {borrow.equipment}
                            </Typography>
                            <Typography variant="h4" fontWeight={700}>
                              {statusText[borrow.status]}
                            </Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between" p="1rem 0 0 0">
                            <Typography>
                              Borrow Date: {borrow.date}
                            </Typography>
                            <Typography variant="h5" fontWeight={700} color="orangered">
                              Duration: {borrow.duration}
                            </Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between" p="1rem 0 1rem 0">
                            <Typography>
                              Approved/Rejected Date: {borrow.supervisor_appr_date || borrow.rejected_date || 'N/A'}
                            </Typography>
                          </Box>
                          <Typography>Destination: {borrow.destination}</Typography>
                          <Typography>{borrow.description || 'No description'}</Typography>
                          {borrow.status === 0 && (
                            <Box display="flex" gap={1} mt={2}>
                              <Button
                                variant="outlined"
                                color="success"
                                size="small"
                                startIcon={<EditIcon />}
                                onClick={() => handleEditBorrow(borrow)}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                startIcon={<DeleteIcon />}
                                onClick={() => {
                                  setSelectedBorrowId(borrow.id);
                                  setOpenCancelDialog(true);
                                }}
                              >
                                Cancel
                              </Button>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Box>
                  ))
                ) : (
                  <Typography>No Current Borrow Requests</Typography>
                )}
              </Box>
            </Box>
          </Box>

          <Box mt={4}>
            <Typography variant="h5" fontWeight={700}>Borrowing History</Typography>
            <Box mt={2} height="400px" overflow={'auto'}>
              <DataGrid
                rows={borrows}
                columns={columns}
                pageSize={10}
                rowsPerPageOptions={[10, 20, 50]}
                components={{ Toolbar: GridToolbarQuickFilter }}
                sx={{
                  '& .MuiDataGrid-root': { border: 'none' },
                  '& .MuiDataGrid-cell': { borderBottom: `1px solid ${colors.primary[200]}` },
                  '& .MuiDataGrid-columnHeaders': { backgroundColor: colors.blue[700], color: 'white' },
                  '& .MuiDataGrid-footerContainer': { backgroundColor: colors.primary[100] }
                }}
              />
            </Box>
          </Box>

          <Dialog open={openBorrowDialog} onClose={() => setOpenBorrowDialog(false)} maxWidth="sm" fullWidth>
            <DialogTitle>{isEditMode ? 'Edit Borrow Request' : 'Borrow Equipment'}</DialogTitle>
            <DialogContent>
              <Box component="form" onSubmit={handleBorrowSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                <TextField
                  label="Equipment (ID)"
                  name="equipment"
                  value={formData.equipment}
                  onChange={handleFormChange}
                  required
                />
                <TextField
                  label="Duration"
                  name="duration"
                  value={formData.duration}
                  onChange={handleFormChange}
                  required
                />
                <TextField
                  select
                  label="Borrow Type"
                  name="type"
                  value={formData.type}
                  onChange={handleFormChange}
                  SelectProps={{ native: true }}
                  required
                >
                  <option value=""></option>
                  <option value="WorkHours">Working Hours</option>
                  <option value="Loaning">Loaning</option>
                </TextField>
                <TextField
                  select
                  label="Destination"
                  name="destination"
                  value={formData.destination}
                  onChange={handleFormChange}
                  SelectProps={{ native: true }}
                  required
                >
                  <option value=""></option>
                  <option value="Work">Work</option>
                  <option value="Outofwork">Out Of Office</option>
                </TextField>
                <TextField
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleFormChange}
                  multiline
                  rows={2}
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenBorrowDialog(false)}>Cancel</Button>
              <Button
                type="submit"
                onClick={handleBorrowSubmit}
                variant="contained"
                sx={{ backgroundColor: colors.blue[400] }}
                endIcon={<SendIcon />}
              >
                {isEditMode ? 'Update' : 'Submit'}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog open={openCancelDialog} onClose={() => setOpenCancelDialog(false)} maxWidth="xs" fullWidth>
            <DialogTitle>Confirm Cancellation</DialogTitle>
            <DialogContent>
              <Typography>Are you sure you want to cancel this borrow request?</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenCancelDialog(false)}>No</Button>
              <Button
                onClick={handleCancelBorrow}
                variant="contained"
                color="error"
                endIcon={<DeleteIcon />}
              >
                Yes, Cancel
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
};

export default Borrow;